import { createListenerMiddleware, createSlice } from '@reduxjs/toolkit';
import { ClientModel, UserModel } from 'api/models';
import pick from 'lodash.pick';
import moment from 'moment';

const pickUserProperties = (obj: any) => {
    return pick(obj, [
        '_id',
        'name',
        'username',
        'email',
        'phone',
        'role',
        'dpUrl',
        'designation',
        'preferences',
        'projectSortOrder',
        'preset',
        'projectAssetPermissions',
    ]);
};

const pickClientProperties = (obj: any) => {
    return pick(obj, ['_id', 'name', 'logoUrl', 'preferences', 'preset', 'plan']);
};

type IAuthUser = Pick<
    UserModel,
    | '_id'
    | 'name'
    | 'username'
    | 'email'
    | 'phone'
    | 'role'
    | 'dpUrl'
    | 'designation'
    | 'preferences'
    | 'projectSortOrder'
    | 'preset'
    | 'projectAssetPermissions'
>;

type IAuthClient = Pick<ClientModel, '_id' | 'name' | 'logoUrl' | 'preferences' | 'preset' | 'plan'>;

type IAuthData = {
    token: string;
    expiryInMinutes: number;
    // rememberMe: boolean;
    user: IAuthUser;
    client: IAuthClient;
};

const initialState: IAuthData = {
    token: null,
    expiryInMinutes: null,
    // rememberMe: false,
    user: null,
    client: null,
};

const TOKEN_KEY = 'pc_token';
const TOKEN_EXPIRY_IN_MINUTES = 'pc_token_expiry_in_minutes';

const getInitialState = (): IAuthData => {
    return {
        token: localStorage.getItem(TOKEN_KEY) || null,
        expiryInMinutes: +localStorage.getItem(TOKEN_EXPIRY_IN_MINUTES) || null,
        user: null,
        client: null,
    };
};

const authSlice = createSlice({
    name: 'auth',
    initialState: getInitialState(),
    reducers: {
        login(state, action) {
            const { token, expiryInMinutes } = action.payload;

            state.token = token;
            state.expiryInMinutes = expiryInMinutes;
        },
        setUser(state, action) {
            const user = pickUserProperties(action.payload);

            state.user = user;
        },
        setClient(state, action) {
            const client = pickClientProperties(action.payload);

            state.client = client;
        },
        setUserAndClient(state, action) {
            const user = pickUserProperties(action.payload.user);
            const client = pickClientProperties(action.payload.client);

            state.user = user;
            state.client = client;
        },
        logout() {
            return initialState;
        },
    },
});

const TOKEN_EXPIRY = 'pc_token_expiry';

export const startSession = (expiryInMinutes: number) => {
    const expiry =
        expiryInMinutes === -1
            ? moment.utc().add(100, 'years').format('YYYYMMDDHHmmss')
            : moment.utc().add(expiryInMinutes, 'minutes').format('YYYYMMDDHHmmss');

    localStorage.setItem(TOKEN_EXPIRY, expiry);
};

export const isSessionExpired = () => {
    if (!localStorage.getItem(TOKEN_EXPIRY)) return true;

    return +moment.utc().format('YYYYMMDDHHmmss') > +localStorage.getItem(TOKEN_EXPIRY);
};

export const endSession = () => {
    localStorage.removeItem(TOKEN_EXPIRY);
};

export const authActions = authSlice.actions;

export const authListenerMiddleware = createListenerMiddleware();

/* ****************** Listen for login ****************** */
authListenerMiddleware.startListening({
    actionCreator: authActions.login,
    effect: (action) => {
        const payload = action.payload;

        localStorage.setItem(TOKEN_KEY, payload.token);
        localStorage.setItem(TOKEN_EXPIRY_IN_MINUTES, payload.expiryInMinutes);

        startSession(payload.expiryInMinutes);
    },
});

/* ****************** Listen for logout ****************** */
authListenerMiddleware.startListening({
    actionCreator: authActions.logout,
    effect: () => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_IN_MINUTES);

        endSession();
    },
});

export default authSlice;
